import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ChevronLeft, ChevronRight, Plus, Search, Filter } from 'lucide-react';
import { CAL } from '@/constants/testIds';

const VIEWS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'resource', label: 'Resource' },
  { value: 'agenda', label: 'Agenda' },
];

function formatCursor(view, d) {
  const monthYear = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  if (view === 'week' || view === 'resource') {
    return `Week of ${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  if (view === 'agenda') return `Agenda from ${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`;
  return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

export default function TopCommandBar({
  view, setView, cursor, setCursor, filters, setFilters,
  clinics, employees, onCreate,
}) {
  const shift = (n) => {
    const x = new Date(cursor);
    const unit = view === 'week' || view === 'resource' ? 7 : (view === 'agenda' ? 7 : 1);
    x.setDate(x.getDate() + n * unit);
    setCursor(x);
  };

  return (
    <div
      className="bg-white border-b border-stone-200 px-4 py-2.5 flex items-center flex-wrap gap-3"
      data-testid={CAL.topBar}
    >
      <div className="flex items-center gap-1">
        <Button
          variant="outline" size="sm"
          className="h-8 border-stone-300"
          onClick={() => shift(-1)}
          data-testid={CAL.prevButton}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button
          variant="outline" size="sm"
          className="h-8 border-stone-300"
          onClick={() => setCursor(new Date())}
          data-testid={CAL.todayButton}
        >
          Today
        </Button>
        <Button
          variant="outline" size="sm"
          className="h-8 border-stone-300"
          onClick={() => shift(1)}
          data-testid={CAL.nextButton}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      <div className="font-heading font-semibold text-stone-800 text-base ml-2 min-w-[220px]">
        {formatCursor(view, cursor)}
      </div>

      <div className="flex items-center gap-2 ml-4" data-testid={CAL.viewSelector}>
        {VIEWS.map((v) => (
          <button
            key={v.value}
            onClick={() => setView(v.value)}
            data-testid={`view-${v.value}`}
            className={`px-3 py-1 text-xs uppercase tracking-wider font-semibold rounded-md border transition-colors ${
              view === v.value
                ? 'bg-stone-900 text-white border-stone-900'
                : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      <div className="flex-1" />

      <div className="flex items-center gap-2">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            placeholder="Search patient…"
            className="h-8 pl-8 w-48 border-stone-300"
            data-testid={CAL.searchInput}
          />
        </div>

        <Select
          value={filters.clinic_id || 'all'}
          onValueChange={(v) => setFilters({ ...filters, clinic_id: v === 'all' ? '' : v })}
        >
          <SelectTrigger className="h-8 w-40 border-stone-300 text-xs" data-testid={CAL.clinicFilter}>
            <SelectValue placeholder="All clinics" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clinics</SelectItem>
            {clinics.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.audiologist_id || 'all'}
          onValueChange={(v) => setFilters({ ...filters, audiologist_id: v === 'all' ? '' : v })}
        >
          <SelectTrigger className="h-8 w-44 border-stone-300 text-xs" data-testid={CAL.audiologistFilter}>
            <SelectValue placeholder="All staff" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Staff</SelectItem>
            {employees.filter(e => e.role === 'audiologist').map((e) => (
              <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          onClick={onCreate}
          data-testid={CAL.createAppointmentButton}
          size="sm"
          className="h-8 bg-stone-900 hover:bg-stone-800 text-white"
        >
          <Plus className="w-4 h-4 mr-1" /> New Appointment
        </Button>
      </div>
    </div>
  );
}
